import { get_token } from "@/router";
import {
  remove_organization_branding,
  save_organization_branding,
} from "@/utils/organization_profile";
import Swal from "sweetalert2";
import {
  apply_organization_branding,
  delete_company_profile_image,
  get_company_profile_data,
  update_company_profile_details,
  upload_company_profile_image,
} from "./organization_profile_setup_api";

function show_profile_setup_toast(options) {
  return Swal.fire({
    showConfirmButton: false,
    position: "bottom-end",
    toast: true,
    ...options,
  });
}

export default {
  clear_platform_branding_overrides() {
    const root = document.documentElement;
    [
      "--primary-color",
      "--secondary-color",
      "--tertiary-color",
      "--accent-color",
      "--banner-gradient",
      "--client-bg-color",
    ].forEach((property) => {
      root.style.removeProperty(property);
    });
  },
  get_platform_default_branding() {
    const styles = getComputedStyle(document.documentElement);
    const primary_color = this.normalize_default_color(styles.getPropertyValue("--primary-color").trim(), "#0d6efd");
    const secondary_color = this.normalize_default_color(styles.getPropertyValue("--secondary-color").trim(), primary_color);
    const tertiary_color = this.normalize_default_color(styles.getPropertyValue("--tertiary-color").trim(), secondary_color);

    return ({
      primary_color,
      secondary_color,
      tertiary_color,
    });
  },
  normalize_default_color(value, fallback) {
    const color = String(value || "").trim();

    if (/^#[0-9a-f]{3,8}$/i.test(color)) return color;
    if (/^rgb/i.test(color)) return color;

    return fallback;
  },
  apply_platform_default_branding() {
    this.clear_platform_branding_overrides();

    const defaults = this.get_platform_default_branding();
    this.form.primary_color = defaults.primary_color;
    this.form.secondary_color = defaults.secondary_color;
    this.form.tertiary_color = defaults.tertiary_color;
    this.form.logo_preview = "";
    this.form.cover_preview = "";
    this.logo_file = null;
    this.cover_file = null;
  },
  apply_branding_to_platform(branding) {
    apply_organization_branding(branding);
  },
  async upload_company_logo() {
    if (!this.logo_file) return this.form.logo_preview;

    const uploaded_url = await upload_company_profile_image({
      file: this.logo_file,
      company_id: this.get_organization_id(),
      endpoint: "/company_management/upload_company_logo/",
      missing_company_message: "Organisation id is missing. Cannot upload logo.",
      upload_error_message: "Logo upload failed.",
    });

    if (!uploaded_url) {
      throw new Error("Logo upload did not return a file URL.");
    }

    this.form.logo_preview = uploaded_url;
    this.logo_file = null;
    return uploaded_url;
  },
  async upload_company_cover() {
    if (!this.cover_file) return this.form.cover_preview;

    const uploaded_url = await upload_company_profile_image({
      file: this.cover_file,
      company_id: this.get_organization_id(),
      endpoint: "/company_management/upload_company_cover/",
      missing_company_message: "Organisation id is missing. Cannot upload cover.",
      upload_error_message: "Cover upload failed.",
    });

    if (uploaded_url) {
      this.form.cover_preview = uploaded_url;
    }

    this.cover_file = null;
    return uploaded_url;
  },
  delete_company_logo() {
    return this.delete_company_profile_image("logo", "Logo delete failed.");
  },
  delete_company_cover() {
    return this.delete_company_profile_image("cover_picture", "Cover delete failed.");
  },
  delete_company_profile_image(file_type, error_message) {
    return delete_company_profile_image(this.get_organization_id(), file_type, error_message);
  },
  fetch_company_profile() {
    const organization_id = this.get_organization_id();

    if (!organization_id || !get_token()) {
      return Promise.resolve(null);
    }

    return new Promise((resolve, reject) => {
      $.ajax({
        url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/get_company_profile/`,
        type: "POST",
        headers: {
          Authorization: `Token ${get_token()}`,
          "Content-Type": "application/json",
        },
        data: JSON.stringify({ organization_id }),
        success: (response) => {
          this.update_company_profile(get_company_profile_data(response));
          resolve(this.company);
        },
        error: (xhr) => {
          this.company = {};
          reject(xhr);
        },
      });
    });
  },
  update_company_profile(company_data) {
    this.company = company_data || {};
    this.prefill_form();
    this.apply_branding_to_platform(this.get_branding_payload());
    this.$nextTick(this.initialize_location_autocomplete);
  },
  async save_branding() {
    this.saving_profile = true;

    try {
      await this.persist_profile_setup();
      this.show_branding_saved();
      return true;
    } catch (error) {
      this.show_branding_error(error);
      return false;
    } finally {
      this.saving_profile = false;
    }
  },
  async persist_profile_setup() {
    await update_company_profile_details(this.get_profile_details_payload());
    this.sync_saved_profile_details();
    await this.upload_company_logo();
    await this.upload_company_cover();
    this.persist_branding_payload(this.get_branding_payload());
    await this.fetch_company_profile().catch(() => null);
  },
  persist_branding_payload(payload) {
    save_organization_branding(payload, this.get_branding_context());
    this.saved_branding = payload;
    this.apply_branding_to_platform(payload);
    this.dispatch_organization_updates(payload);
  },
  dispatch_organization_updates(payload) {
    window.dispatchEvent(new CustomEvent("organization-branding-updated", { detail: payload }));
    window.dispatchEvent(new CustomEvent("organization-profile-updated", { detail: this.company }));
  },
  show_branding_saved() {
    show_profile_setup_toast({
      icon: "success",
      title: "Branding saved",
      timer: 1200,
    });
  },
  show_branding_error(error) {
    show_profile_setup_toast({
      icon: "error",
      title: "Branding upload failed",
      text: error.message || "Please try again.",
      timer: 3000,
    });
  },
  async reset_branding() {
    this.saving_profile = true;

    try {
      await this.reset_branding_assets();
      this.show_branding_reset();
    } catch (error) {
      this.show_branding_reset_error(error);
    } finally {
      this.saving_profile = false;
    }
  },
  async reset_branding_assets() {
    await Promise.all([this.delete_company_logo(), this.delete_company_cover()]);
    await this.clear_persisted_branding_details();
    remove_organization_branding(this.get_branding_context());
    this.clear_branding_storage_values();
    this.clear_branding_user_details();
    this.saved_branding = null;
    this.apply_platform_default_branding();
    await this.fetch_company_profile().catch(() => null);
    this.apply_platform_default_branding();
    this.dispatch_branding_reset();
  },
  clear_persisted_branding_details() {
    return update_company_profile_details({
      ...this.get_profile_details_payload(),
      primary_color: "",
      secondary_color: "",
      tertiary_color: "",
    });
  },
  clear_branding_storage_values() {
    [
      "organization_logo",
      "organization_primary_color",
      "organization_secondary_color",
      "organization_tertiary_color",
    ].forEach((key) => {
      localStorage.removeItem(key);
    });
  },
  clear_branding_user_details() {
    this.user_details = {
      ...(this.user_details || {}),
      organization_logo: "",
      organization_primary_color: "",
      organization_secondary_color: "",
      organization_tertiary_color: "",
    };
  },
  dispatch_branding_reset() {
    window.dispatchEvent(new CustomEvent("organization-branding-updated", {
      detail: {
        reset: true,
        organization_id: this.get_organization_id(),
        logo_preview: "",
        cover_preview: "",
      },
    }));
    window.dispatchEvent(new CustomEvent("organization-profile-updated", { detail: this.company }));
  },
  show_branding_reset() {
    show_profile_setup_toast({
      icon: "success",
      title: "Branding reset",
      timer: 1200,
    });
  },
  show_branding_reset_error(error) {
    show_profile_setup_toast({
      icon: "error",
      title: "Branding reset failed",
      text: error.message || "Please try again.",
      timer: 3000,
    });
  },
  delete_branding() {
    remove_organization_branding(this.get_branding_context());
    this.saved_branding = null;
    this.reset_branding();
  },
  async save_profile_setup() {
    const saved = await this.save_branding();

    if (saved) {
      this.$router.push({ name: "Home" });
    }
  },
};
